import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { FiX, FiCheck, FiRotateCw, FiZoomIn, FiUpload } from 'react-icons/fi';
import getCroppedImg from '../utils/cropUtils';

const ImageCropper = ({ imageSrc, onCancel, onCropComplete }) => {
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [rotation, setRotation] = useState(0);
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

    const onCropChange = (crop) => {
        setCrop(crop);
    };

    const onRotationChange = (rotation) => {
        setRotation(rotation);
    };

    const onZoomChange = (zoom) => {
        setZoom(zoom);
    };

    const onInteractionEnd = (croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    };

    const handleSave = async () => {
        try {
            const croppedImage = await getCroppedImg(
                imageSrc,
                croppedAreaPixels,
                rotation
            );
            onCropComplete(croppedImage);
        } catch (e) {
            console.error(e);
        }
    };

    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[340px] overflow-hidden flex flex-col h-auto max-h-[90vh] border border-white/20">
                <div className="px-4 py-2.5 border-b flex justify-between items-center bg-gray-50/80">
                    <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-red-50 text-[#C30010] rounded-lg">
                            <FiUpload size={14} />
                        </div>
                        <h3 className="font-bold text-gray-800 text-[12px] uppercase tracking-wider">Edit Image</h3>
                    </div>
                    <button onClick={onCancel} className="p-1.5 hover:bg-gray-200 rounded-full transition-colors text-gray-400">
                        <FiX size={16} />
                    </button>
                </div>

                <div className="relative h-72 w-full bg-slate-950">
                    <Cropper
                        image={imageSrc}
                        crop={crop}
                        rotation={rotation}
                        zoom={zoom}
                        aspect={1}
                        onCropChange={onCropChange}
                        onRotationChange={onRotationChange}
                        onCropComplete={onInteractionEnd}
                        onZoomChange={onZoomChange}
                        showGrid={true}
                    />
                </div>

                <div className="p-3.5 flex flex-col gap-3 bg-white">
                    <div className="flex flex-col gap-1.5">
                        <div className="flex justify-between text-[9px] font-bold text-gray-400 uppercase tracking-widest pl-0.5">
                            <span className="flex items-center gap-1.5">Zoom</span>
                            <span className="text-[#C30010] font-mono">{zoom.toFixed(1)}x</span>
                        </div>
                        <input
                            type="range"
                            value={zoom}
                            min={1}
                            max={3}
                            step={0.1}
                            aria-labelledby="Zoom"
                            onChange={(e) => setZoom(Number(e.target.value))}
                            className="w-full h-1 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-[#C30010]"
                        />
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <div className="flex justify-between text-[9px] font-bold text-gray-400 uppercase tracking-widest pl-0.5">
                            <span className="flex items-center gap-1.5">Rotation</span>
                            <span className="text-[#C30010] font-mono">{rotation}°</span>
                        </div>
                        <input
                            type="range"
                            value={rotation}
                            min={0}
                            max={360}
                            step={1}
                            aria-labelledby="Rotation"
                            onChange={(e) => setRotation(Number(e.target.value))}
                            className="w-full h-1 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-[#C30010]"
                        />
                    </div>

                    <div className="flex gap-2.5 mt-1.5">
                        <button
                            onClick={onCancel}
                            className="flex-1 py-2 text-[11px] font-bold text-gray-500 bg-gray-50 border border-gray-100 rounded-xl hover:bg-gray-100 transition-all uppercase tracking-wider"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            className="flex-[1.5] py-2 text-[11px] font-bold text-white bg-[#C30010] hover:bg-[#a0000d] rounded-xl shadow-md transition-all flex items-center justify-center gap-1.5 uppercase tracking-wider"
                        >
                            <FiCheck size={14} /> Confirm
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ImageCropper;
